// Auth context — single source of truth for the bearer's identity.
//
// httpOnly cookies (voc_access + voc_refresh) are now the only token transport.
// JS cannot read them; the browser attaches them automatically on every fetch
// to api.vocaler.ai because every fetch sets credentials:"include". This
// context only tracks the *identity* (user row from /me-public), not the
// tokens themselves. Hydration is "ask /me-public and trust the response":
// a 401 means the cookie's missing/expired and we bounce to /login.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Navigate, useLocation } from "react-router-dom";
import { auth as authFetch, http, onUnauthorized } from "../../services/client";
import { ApiError, type User } from "../../types";

export type AuthStatus = "loading" | "anonymous" | "authenticated";

interface SignupInput {
  email: string;
  password: string;
  tenant_name: string;
  plan?: "free" | "pro" | "studio";
}

interface LoginResponse {
  // Server still returns tokens in the body for back-compat but the SPA
  // ignores them — the same response carries the Set-Cookie header that
  // installs voc_access + voc_refresh on api.vocaler.ai. We only care about
  // the MFA short-circuit.
  mfa_required?: boolean;
  challenge_token?: string;
}

export interface MfaChallenge {
  challengeToken: string;
}

interface MeResponse {
  user_id: string;
  email: string;
  tenant_id: string;
  role: "owner" | "admin" | "member";
  email_verified_at: string | null;
  is_operator: boolean;
}

interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  /**
   * Returns `null` on a clean sign-in; returns an {@link MfaChallenge} when
   * the account requires 2FA — the caller routes to the TOTP / passkey
   * verify screen and calls {@link completeMfa}.
   */
  login(email: string, password: string): Promise<MfaChallenge | null>;
  /**
   * Complete a 2FA login. The /auth/2fa/totp/verify (and recovery / passkey)
   * endpoint already installed the httpOnly cookies on its response — this
   * just re-hydrates the identity.
   */
  completeMfa(): Promise<void>;
  signup(input: SignupInput): Promise<void>;
  logout(): Promise<void>;
  /** Force a re-hydrate from /auth/me-public. */
  refresh(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function initialsOf(name: string, email: string): string {
  const base = (name || (email.split("@", 1)[0] ?? email)).replace(/[._]+/g, " ").trim();
  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return base.slice(0, 2).toUpperCase();
  const first = parts[0] ?? "";
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? "";
  return ((first[0] ?? "") + (last[0] ?? "")).toUpperCase();
}

function userFromMe(me: MeResponse): User {
  const local = me.email.split("@", 1)[0] ?? me.email;
  const name = local.replace(/[._]+/g, " ");
  const title = name
    .split(" ")
    .map((w) => (w ? (w[0]?.toUpperCase() ?? "") + w.slice(1) : ""))
    .join(" ");
  return {
    id: me.user_id,
    name: title,
    initials: initialsOf(title, me.email),
    email: me.email,
    isOperator: Boolean(me.is_operator),
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<User | null>(null);
  const mounted = useRef(true);

  const hydrate = useCallback(async () => {
    // Cookie presence is invisible to JS — we don't try to detect it. Just
    // ask the server: /me-public is the cheapest authenticated endpoint.
    // A 401 means there's no valid session cookie; treat as anonymous.
    try {
      const me = await http<MeResponse>("/account/me").catch(async (err) => {
        if (err instanceof ApiError && err.status === 404) {
          return await authFetch<MeResponse>("/me-public");
        }
        throw err;
      });
      const looksLikeUser = "name" in (me as unknown as Record<string, unknown>);
      const next = looksLikeUser ? ((me as unknown) as User) : userFromMe(me);
      if (!mounted.current) return;
      setUser(next);
      setStatus("authenticated");
    } catch (err) {
      if (!mounted.current) return;
      // 401 / network failure / etc. → anonymous; the AuthGate will route.
      setUser(null);
      setStatus("anonymous");
      void err;
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    onUnauthorized(() => {
      setUser(null);
      setStatus("anonymous");
    });
    void hydrate();
    return () => {
      mounted.current = false;
    };
  }, [hydrate]);

  const login = useCallback(
    async (email: string, password: string): Promise<MfaChallenge | null> => {
      const body = await authFetch<LoginResponse>("/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      if (body.mfa_required && body.challenge_token) {
        return { challengeToken: body.challenge_token };
      }
      // The login response set voc_access + voc_refresh cookies. Just hydrate.
      await hydrate();
      return null;
    },
    [hydrate],
  );

  const completeMfa = useCallback(
    async () => {
      // The TOTP / passkey verify endpoint already installed cookies via
      // Set-Cookie on its response. Just re-hydrate identity.
      await hydrate();
    },
    [hydrate],
  );

  const signup = useCallback(
    async (input: SignupInput) => {
      await authFetch("/signup", {
        method: "POST",
        body: JSON.stringify({
          email: input.email,
          password: input.password,
          tenant_name: input.tenant_name,
          plan: input.plan ?? "free",
        }),
      });
      await login(input.email, input.password);
    },
    [login],
  );

  const logout = useCallback(async () => {
    try {
      // The server clears voc_access + voc_refresh in its Set-Cookie header.
      await authFetch("/logout", { method: "POST" });
    } catch {
      // Best-effort; UI flips to anonymous regardless.
    }
    setUser(null);
    setStatus("anonymous");
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, login, signup, logout, completeMfa, refresh: hydrate }),
    [status, user, login, signup, logout, completeMfa, hydrate],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth() must be used inside <AuthProvider>");
  return ctx;
}

/** Gate. Renders children once authenticated; redirects to /login otherwise. */
export function AuthGate({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === "loading") {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: "var(--bg)",
          color: "var(--fg-muted)",
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          letterSpacing: "0.02em",
        }}
      >
        loading…
      </div>
    );
  }
  if (status === "anonymous") {
    const search = new URLSearchParams({ next: location.pathname + location.search });
    return <Navigate to={`/login?${search.toString()}`} replace />;
  }
  return <>{children}</>;
}
